/**
 * Connector synchronization utilities
 * Updates connector anchors when connected elements move
 */

import { EditableElement, ConnectorElement, AnchorPoint } from '../editing/types';
import { calculateAnchorPoints, findBestAnchor, calculatePreciseAnchor } from './connectorUtils';

/**
 * Update connector anchors when an element moves
 */
export function updateConnectorAnchors(
  connectors: ConnectorElement[],
  movedElementId: string,
  movedElement: EditableElement
): ConnectorElement[] {
  return connectors.map((connector) => {
    const data = connector.data;
    let updated = false;
    const updates: Partial<ConnectorElement['data']> = {};

    // Update start anchor if connected to moved element
    if (data.startElementId === movedElementId) {
      if (data.startAnchor.isPrecise) {
        // For precise anchors, recalculate based on relative position
        const relativeX = data.startAnchor.x - (connector.x || 0);
        const relativeY = data.startAnchor.y - (connector.y || 0);
        const newAnchor = calculatePreciseAnchor(
          {
            x: movedElement.x + relativeX,
            y: movedElement.y + relativeY,
          },
          movedElement
        );
        updates.startAnchor = newAnchor;
      } else {
        // For standard anchors, recalculate based on side
        const anchors = calculateAnchorPoints(movedElement);
        const anchor = anchors.find((a) => a.side === data.startAnchor.side);
        if (anchor) {
          updates.startAnchor = {
            ...data.startAnchor,
            x: anchor.x,
            y: anchor.y,
          };
        }
      }
      updated = true;
    }

    // Update end anchor if connected to moved element
    if (data.endElementId === movedElementId) {
      if (data.endAnchor.isPrecise) {
        // For precise anchors, recalculate based on relative position
        const relativeX = data.endAnchor.x - (connector.x || 0);
        const relativeY = data.endAnchor.y - (connector.y || 0);
        const newAnchor = calculatePreciseAnchor(
          {
            x: movedElement.x + relativeX,
            y: movedElement.y + relativeY,
          },
          movedElement
        );
        updates.endAnchor = newAnchor;
      } else {
        // For standard anchors, recalculate based on side
        const anchors = calculateAnchorPoints(movedElement);
        const anchor = anchors.find((a) => a.side === data.endAnchor.side);
        if (anchor) {
          updates.endAnchor = {
            ...data.endAnchor,
            x: anchor.x,
            y: anchor.y,
          };
        }
      }
      updated = true;
    }

    // If both elements are connected, recalculate best anchors
    if (data.startElementId && data.endElementId && (data.startElementId === movedElementId || data.endElementId === movedElementId)) {
      const startElement = data.startElementId === movedElementId 
        ? movedElement 
        : connectors.find(c => c.id === data.startElementId) || 
          (connector as any).__startElement; // Fallback - would need to pass elements array
      const endElement = data.endElementId === movedElementId 
        ? movedElement 
        : connectors.find(c => c.id === data.endElementId) || 
          (connector as any).__endElement; // Fallback

      // Only update if we have both elements (this is a simplified version)
      // In practice, you'd pass the full elements array
    }

    if (updated) {
      // Recalculate connector's bounding box based on updated anchor positions
      const updatedStartAnchor = updates.startAnchor || data.startAnchor;
      const updatedEndAnchor = updates.endAnchor || data.endAnchor;
      
      const minX = Math.min(updatedStartAnchor.x, updatedEndAnchor.x);
      const minY = Math.min(updatedStartAnchor.y, updatedEndAnchor.y);
      const maxX = Math.max(updatedStartAnchor.x, updatedEndAnchor.x);
      const maxY = Math.max(updatedStartAnchor.y, updatedEndAnchor.y);
      
      return {
        ...connector,
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY,
        data: {
          ...data,
          ...updates,
        },
      };
    }

    return connector;
  });
}

/**
 * Update connector anchors with full element context
 */
export function syncConnectorsWithElements(
  connectors: ConnectorElement[],
  elements: EditableElement[],
  movedElementId: string
): ConnectorElement[] {
  const movedElement = elements.find((el) => el.id === movedElementId);
  if (!movedElement) return connectors;

  return connectors.map((connector) => {
    const data = connector.data;
    let updated = false;
    const updates: Partial<ConnectorElement['data']> = {};

    // Update start anchor if connected to moved element
    if (data.startElementId === movedElementId) {
      if (data.startAnchor.isPrecise) {
        // For precise anchors, maintain relative position
        // Calculate the offset from the element's original position
        // This is a simplified version - in practice you'd store the original element position
        const anchors = calculateAnchorPoints(movedElement);
        const anchor = anchors.find((a) => a.side === data.startAnchor.side);
        if (anchor) {
          // Try to maintain the precise position relative to the side
          updates.startAnchor = {
            ...data.startAnchor,
            x: anchor.x,
            y: anchor.y,
          };
        }
      } else {
        // For standard anchors, recalculate based on side
        const anchors = calculateAnchorPoints(movedElement);
        const anchor = anchors.find((a) => a.side === data.startAnchor.side);
        if (anchor) {
          updates.startAnchor = {
            ...data.startAnchor,
            x: anchor.x,
            y: anchor.y,
          };
        }
      }
      updated = true;
    }

    // Update end anchor if connected to moved element
    if (data.endElementId === movedElementId) {
      if (data.endAnchor.isPrecise) {
        // For precise anchors, maintain relative position
        const anchors = calculateAnchorPoints(movedElement);
        const anchor = anchors.find((a) => a.side === data.endAnchor.side);
        if (anchor) {
          updates.endAnchor = {
            ...data.endAnchor,
            x: anchor.x,
            y: anchor.y,
          };
        }
      } else {
        // For standard anchors, recalculate based on side
        const anchors = calculateAnchorPoints(movedElement);
        const anchor = anchors.find((a) => a.side === data.endAnchor.side);
        if (anchor) {
          updates.endAnchor = {
            ...data.endAnchor,
            x: anchor.x,
            y: anchor.y,
          };
        }
      }
      updated = true;
    }

    // If both elements are connected, optionally recalculate best anchors
    if (data.startElementId && data.endElementId) {
      const startElement = elements.find((el) => el.id === data.startElementId);
      const endElement = elements.find((el) => el.id === data.endElementId);

      if (startElement && endElement) {
        // Only recalculate if one of them moved
        if (data.startElementId === movedElementId || data.endElementId === movedElementId) {
          // Optionally update to best anchor (commented out to preserve user's anchor choice)
          // const bestEndAnchor = findBestAnchor(startElement, endElement, data.startAnchor);
          // updates.endAnchor = bestEndAnchor;
        }
      }
    }

    if (updated) {
      // Recalculate connector's bounding box based on updated anchor positions
      const updatedStartAnchor = updates.startAnchor || data.startAnchor;
      const updatedEndAnchor = updates.endAnchor || data.endAnchor;
      
      const minX = Math.min(updatedStartAnchor.x, updatedEndAnchor.x);
      const minY = Math.min(updatedStartAnchor.y, updatedEndAnchor.y);
      const maxX = Math.max(updatedStartAnchor.x, updatedEndAnchor.x);
      const maxY = Math.max(updatedStartAnchor.y, updatedEndAnchor.y);
      
      return {
        ...connector,
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY,
        data: {
          ...data,
          ...updates,
        },
      };
    }

    return connector;
  });
}

